import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Play, CheckCircle, Lock, ArrowLeft, Clock, FileText, Volume2, Download, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { useCourse } from '../contexts/CourseContext';

const Learn = () => {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const { getCourseBySlug, updateCourseProgress } = useCourse();
  
  const [currentLesson, setCurrentLesson] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [watchedVideoIds, setWatchedVideoIds] = useState(new Set());
  const videoRef = useRef(null);
  const watchedVideoIdsRef = useRef(new Set());

  // Récupérer le cours depuis le contexte en utilisant le slug
  const course = getCourseBySlug(courseSlug);

  // Charge les vidéos déjà visionnées depuis le serveur
  useEffect(() => {
    if (!course?.id) return;
    axios.get(`/api/courses/${course.id}/progress`)
      .then(res => {
        const unwatchedIds = new Set(
          (res.data?.progress?.unwatched_video_lessons || []).map(l => l.id)
        );
        const watchedIds = new Set(
          (course.lessons || [])
            .filter(l => (l.type === 'video') && !unwatchedIds.has(l.id))
            .map(l => l.id)
        );
        watchedVideoIdsRef.current = watchedIds;
        setWatchedVideoIds(watchedIds);
      })
      .catch(() => {});
  }, [course?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const markVideoWatched = useCallback(async (lessonId) => {
    if (watchedVideoIdsRef.current.has(lessonId)) return;
    watchedVideoIdsRef.current.add(lessonId);
    try {
      await axios.post(`/api/lessons/${lessonId}/video-watched`);
      setWatchedVideoIds(prev => new Set([...prev, lessonId]));
    } catch (err) {
      watchedVideoIdsRef.current.delete(lessonId);
    }
  }, []);

  const handleVideoTimeUpdate = useCallback((e, lessonId) => {
    const video = e.target;
    if (!video.duration || watchedVideoIdsRef.current.has(lessonId)) return;
    if (video.currentTime / video.duration >= 0.80) markVideoWatched(lessonId);
  }, [markVideoWatched]);
  
  
  if (!course) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Cours non trouvé</h1>
          <p className="text-gray-600 mb-6">Le cours que vous recherchez n'existe pas.</p>
          <button 
            onClick={() => navigate('/courses')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            Retour aux cours
          </button>
        </div>
      </div>
    );
  }

  const handleLessonComplete = async (lessonId) => {
    setIsLoading(true);
    try {
      const progress = ((currentLesson + 1) / course.lessons.length) * 100;
      await updateCourseProgress(course.id, lessonId, progress);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextLesson = () => {
    if (currentLesson < course.lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
    }
  };


  const currentLessonData = course.lessons[currentLesson];

  // Rendu du contenu principal selon le type de leçon
  const renderLessonContent = (lesson) => {
    const content = lesson.content || {};
    const type = lesson.type || 'video';

    if (type === 'video') {
      if (content.videoUrl) {
        const lessonId = lesson.id || lesson._id;
        return (
          <video
            ref={videoRef}
            key={content.videoUrl}
            controls
            controlsList="nodownload"
            className="w-full h-full"
            poster={content.videoThumbnailUrl || undefined}
            onTimeUpdate={(e) => handleVideoTimeUpdate(e, lessonId)}
            onEnded={() => {
              markVideoWatched(lessonId);
              handleLessonComplete(lessonId);
            }}
          >
            <source src={content.videoUrl} />
            Votre navigateur ne supporte pas la lecture vidéo.
          </video>
        );
      }
      return (
        <div className="text-center text-white">
          <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold mb-2">{lesson.title}</h3>
          <p className="text-gray-300">Vidéo non disponible</p>
        </div>
      );
    }

    if (type === 'audio') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-white gap-6 px-4 sm:px-8">
          <Volume2 className="w-16 h-16 sm:w-20 sm:h-20 opacity-70" />
          <h3 className="text-lg sm:text-xl font-semibold">{lesson.title}</h3>
          {content.audioUrl ? (
            <audio controls className="w-full" onEnded={() => handleLessonComplete(lesson.id || lesson._id)}>
              <source src={content.audioUrl} />
            </audio>
          ) : (
            <p className="text-gray-300">Audio non disponible</p>
          )}
        </div>
      );
    }

    if (type === 'document') {
      const docUrl = content.documentUrl;
      const isOneDrive = docUrl && (
        docUrl.includes('1drv.ms') ||
        docUrl.includes('onedrive.live.com') ||
        docUrl.includes('sharepoint.com')
      );
      const isGDrive = docUrl && (
        docUrl.includes('drive.google.com') ||
        docUrl.includes('docs.google.com')
      );
      const isPdf = docUrl && !isOneDrive && !isGDrive && (
        docUrl.includes('.pdf') ||
        docUrl.includes('cloudinary')
      );

      let embedUrl = null;
      if (isPdf) {
        embedUrl = docUrl;
      } else if (isOneDrive) {
        embedUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docUrl)}`;
      } else if (isGDrive) {
        const fileId = docUrl.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1];
        if (fileId) embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
      }

      return (
        <div className="flex flex-col h-full text-white">
          <div className="flex items-center justify-between px-4 py-3 bg-black/20">
            <h3 className="text-lg font-semibold truncate">{lesson.title}</h3>
            {docUrl && (
              <a
                href={docUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 flex items-center gap-2 bg-white text-gray-900 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors text-sm ml-4"
              >
                <Download className="w-4 h-4" />
                {content.documentName || 'Ouvrir'}
              </a>
            )}
          </div>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              className="flex-1 w-full border-0"
              title={content.documentName || lesson.title}
              allow="autoplay"
            />
          ) : docUrl ? (
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <FileText className="w-20 h-20 opacity-70" />
              <p className="text-gray-300 text-center px-4">
                Cliquez sur "Ouvrir" pour accéder au document
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <FileText className="w-20 h-20 opacity-70" />
              <p className="text-gray-300">Document non disponible</p>
            </div>
          )}
        </div>
      );
    }

    // text / quiz — rendu en dessous du bloc vidéo (zone grise = header)
    return (
      <div className="flex items-center justify-center h-full text-white">
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold">{lesson.title}</h3>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors duration-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </button>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{course.title}</h1>
            <p className="text-gray-600 mb-4">Par {course.instructor}</p>
            
            {/* Barre de progression */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentLesson + 1) / course.lessons.length) * 100}%` }}
              ></div>
            </div>
            
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Leçon {currentLesson + 1} sur {course.lessons.length}</span>
              <span>{Math.round(((currentLesson + 1) / course.lessons.length) * 100)}% terminé</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {/* Contenu principal */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Vidéo/Contenu de la leçon */}
              <div className="aspect-video bg-gray-900 flex items-center justify-center overflow-hidden">
                {renderLessonContent(currentLessonData)}
              </div>
              
              {/* Informations de la leçon */}
              <div className="p-4 sm:p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{currentLessonData.title}</h2>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{currentLessonData.duration}</span>
                  </div>
                  {currentLessonData.isFree && (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                      Gratuit
                    </span>
                  )}
                </div>
                
                {currentLessonData.content?.text ? (
                  <div
                    className="prose prose-gray max-w-none mb-6 text-gray-700"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentLessonData.content.text, {
                      ALLOWED_TAGS: ['p','br','strong','em','ul','ol','li','h1','h2','h3','h4',
                                     'blockquote','code','pre','a','img','table','thead','tbody',
                                     'tr','th','td','span','div'],
                      ALLOWED_ATTR: ['href','src','alt','class','target','rel'],
                      ALLOW_DATA_ATTR: false,
                    }) }}
                  />
                ) : currentLessonData.description ? (
                  <p className="text-gray-700 mb-6">{currentLessonData.description}</p>
                ) : null}
                
                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleLessonComplete(currentLessonData.id)}
                    disabled={isLoading}
                    className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50"
                  >
                    {isLoading ? 'Enregistrement...' : 'Marquer comme terminé'}
                  </button>
                  
                  {(() => {
                    const isLast = currentLesson >= course.lessons.length - 1;
                    const lessonId = currentLessonData?.id || currentLessonData?._id;
                    const isVideoUnwatched = currentLessonData?.type === 'video' && !watchedVideoIds.has(lessonId);
                    return (
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={handleNextLesson}
                          disabled={isLast || isVideoUnwatched}
                          className="border border-primary text-primary px-6 py-2 rounded-lg hover:bg-primary hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Leçon suivante
                        </button>
                        {isVideoUnwatched && !isLast && (
                          <p className="text-xs text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Regardez 80 % de la vidéo pour continuer
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar avec le programme */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Programme du cours</h3>
              
              <div className="space-y-2">
                {course.lessons.map((lesson, index) => (
                  <div
                    key={lesson.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                      index === currentLesson
                        ? 'bg-primary text-white'
                        : index < currentLesson
                        ? 'bg-green-50 text-green-800 hover:bg-green-100'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setCurrentLesson(index)}
                  >
                    <div className="flex items-center gap-3">
                      {index < currentLesson ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : index === currentLesson ? (
                        <Play className="w-4 h-4" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-400" />
                      )}
                      
                      <div className="flex-1">
                        <h4 className="text-sm font-medium">{lesson.title}</h4>
                        <p className="text-xs opacity-75">{lesson.duration}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">
                    {Math.round(((currentLesson + 1) / course.lessons.length) * 100)}%
                  </div>
                  <div className="text-sm text-gray-600">Progression</div>
                </div>
              </div>
              
              {/* Lien vers les évaluations */}
              <div className="mt-4">
                <button
                  onClick={() => navigate(`/course/${course.id}/evaluations`)}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <FileText className="w-4 h-4" />
                  Évaluations
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Learn;
